import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';

import themeColors from 'src/theme/colors';
import type {
  AgreedActivitiesDistrictStatistic,
  AgreedActivitiesHourlyRates,
  AgreedActivitiesSelection,
  AgreedActivityCategory,
  AgreedActivityShape,
} from 'src/components/NeedsAssessment/needsAssessment';

import { SectionTitle } from './SectionTitle';

interface Props {
  categories: AgreedActivityCategory[];
  districtStatistic: AgreedActivitiesDistrictStatistic | null;
  error?: string;
  hourlyRates: AgreedActivitiesHourlyRates | null;
  selection: AgreedActivitiesSelection;
  setFieldValue: (field: string, value: string[]) => void;
}

export const AgreedActivitiesSection: FC<Props> = ({
  categories,
  districtStatistic,
  error,
  hourlyRates,
  selection,
  setFieldValue,
}) => {
  const { i18n, t } = useTranslation('translation');
  const hasCompanionshipOptions = categories.some(
    (category) => category.companionship !== null,
  );
  const selectedCount =
    selection.companionship.length + selection.domesticHelp.length;
  const percentage = selectedCount
    ? (selection.companionship.length / selectedCount) * 100
    : 0;
  const averageHourlyRate =
    selectedCount && hourlyRates
      ? (Number(hourlyRates.companionship) * selection.companionship.length +
          Number(hourlyRates.domesticHelp) * selection.domesticHelp.length) /
        selectedCount
      : null;
  const formattedAverageHourlyRate =
    averageHourlyRate === null
      ? null
      : new Intl.NumberFormat(i18n.language, {
          currency: 'EUR',
          style: 'currency',
        }).format(averageHourlyRate);

  const updateSelection = (
    category: AgreedActivityCategory,
    shapeName: keyof AgreedActivitiesSelection,
    checked: boolean,
  ) => {
    const otherShapeName =
      shapeName === 'companionship' ? 'domesticHelp' : 'companionship';
    const selectedValues = selection[shapeName].filter(
      (value) => value !== category.value,
    );

    setFieldValue(
      `agreedActivities.${shapeName}`,
      checked ? [...selectedValues, category.value] : selectedValues,
    );

    if (checked) {
      setFieldValue(
        `agreedActivities.${otherShapeName}`,
        selection[otherShapeName].filter((value) => value !== category.value),
      );
    }
  };

  const renderShape = (
    category: AgreedActivityCategory,
    shapeName: keyof AgreedActivitiesSelection,
    shape: AgreedActivityShape | null,
  ) => {
    if (!shape) {
      return (
        <Box
          borderTop={`1px solid ${themeColors.shade1}`}
          flexGrow={1}
          px={1.5}
          py={1}
        >
          <Typography color={themeColors.shade3} variant="body2">
            {t('NeedsAssessmentForm.AgreedActivities.unavailable')}
          </Typography>
        </Box>
      );
    }

    const isSelected = selection[shapeName].includes(category.value);

    return (
      <Box
        bgcolor={
          isSelected
            ? shapeName === 'companionship'
              ? themeColors.successBackground
              : themeColors.primary1
            : themeColors.white
        }
        borderTop={`1px solid ${themeColors.shade1}`}
        flexGrow={1}
        minWidth={0}
        px={1}
        py={0.5}
        width="100%"
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isSelected}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
              onChange={(event) =>
                updateSelection(category, shapeName, event.target.checked)
              }
            />
          }
          label={
            <Typography
              color={themeColors.shade5}
              sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              variant="body2"
            >
              {shape.examples.join(', ')}
            </Typography>
          }
          sx={{
            '& .MuiFormControlLabel-label': { minWidth: 0 },
            'alignItems': 'center',
            'margin': 0,
            'minWidth': 0,
            'width': '100%',
          }}
        />
      </Box>
    );
  };

  const renderPercentageIndicators = () => (
    <>
      {hasCompanionshipOptions && districtStatistic && (
        <Box mb={2} position="relative">
          <LinearProgress
            aria-label={t(
              'NeedsAssessmentForm.AgreedActivities.districtPercentageLabel',
            )}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={Number(districtStatistic.percentageValue)}
            color={districtStatistic.targetReached ? 'success' : 'error'}
            sx={{ borderRadius: 1, height: 32 }}
            value={Number(districtStatistic.percentageValue)}
            variant="determinate"
          />
          <Typography
            alignItems="center"
            display="flex"
            fontSize={{ sm: '0.875rem', xs: '0.75rem' }}
            fontWeight={500}
            height="100%"
            justifyContent="center"
            left={0}
            lineHeight={1.2}
            px={1}
            sx={{ overflowWrap: 'anywhere' }}
            position="absolute"
            textAlign="center"
            top={0}
            width="100%"
          >
            {districtStatistic.percentageDisplay}%{' '}
            {t('NeedsAssessmentForm.AgreedActivities.districtCompanionship')}
          </Typography>
        </Box>
      )}

      {hasCompanionshipOptions && (
        <Box mb={2} position="relative">
          <LinearProgress
            aria-label={t(
              'NeedsAssessmentForm.AgreedActivities.percentageLabel',
            )}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={Math.round(percentage)}
            color="success"
            sx={{ borderRadius: 1, height: 32 }}
            value={percentage}
            variant="determinate"
          />
          <Typography
            alignItems="center"
            display="flex"
            fontSize={{ sm: '0.875rem', xs: '0.75rem' }}
            fontWeight={500}
            height="100%"
            justifyContent="center"
            left={0}
            lineHeight={1.2}
            px={1}
            sx={{ overflowWrap: 'anywhere' }}
            position="absolute"
            textAlign="center"
            top={0}
            width="100%"
          >
            {Math.round(percentage)}%{' '}
            {t('NeedsAssessmentForm.AgreedActivities.companionship')}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <>
      <SectionTitle title={t('NeedsAssessmentForm.AgreedActivities.title')} />
      <Grid item xs={12}>
        {error && (
          <Typography
            color={themeColors.error}
            data-error
            fontSize="0.75rem"
            mb={1}
          >
            {error}
          </Typography>
        )}
        {renderPercentageIndicators()}

        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          maxWidth="100%"
          minWidth={0}
          width="100%"
        >
          {categories.map((category) => (
            <Box
              border={`1px solid ${themeColors.shade2}`}
              borderRadius={2}
              key={category.value}
              maxWidth="100%"
              minWidth={0}
              overflow="hidden"
              width="100%"
            >
              <Typography
                bgcolor={themeColors.shade1}
                color={themeColors.primary4}
                fontSize="0.9375rem"
                fontWeight={600}
                px={1.5}
                py={1}
                sx={{ overflowWrap: 'anywhere' }}
              >
                {category.label}
              </Typography>
              <Grid container>
                <Grid
                  item
                  bgcolor={themeColors.white}
                  display="flex"
                  flexDirection="column"
                  minWidth={0}
                  md={6}
                  xs={12}
                >
                  <Typography
                    color={themeColors.shade4}
                    fontSize="0.75rem"
                    fontWeight={600}
                    px={1.5}
                    pt={1}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {t('NeedsAssessmentForm.AgreedActivities.companionship')}
                  </Typography>
                  {renderShape(
                    category,
                    'companionship',
                    category.companionship,
                  )}
                </Grid>
                <Grid
                  item
                  bgcolor={themeColors.white}
                  display="flex"
                  flexDirection="column"
                  minWidth={0}
                  md={6}
                  xs={12}
                >
                  <Typography
                    color={themeColors.shade4}
                    fontSize="0.75rem"
                    fontWeight={600}
                    px={1.5}
                    pt={1}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {t('NeedsAssessmentForm.AgreedActivities.domesticHelp')}
                  </Typography>
                  {renderShape(category, 'domesticHelp', category.domesticHelp)}
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>

        <Box mt={2}>{renderPercentageIndicators()}</Box>

        {formattedAverageHourlyRate && (
          <Box
            bgcolor={themeColors.primary1}
            border={`1px solid ${themeColors.primary2}`}
            borderRadius={2}
            px={2}
            py={1.5}
          >
            <Typography
              color={themeColors.primary4}
              fontWeight={600}
              textAlign="center"
            >
              {t('NeedsAssessmentForm.AgreedActivities.averageHourlyRate', {
                rate: formattedAverageHourlyRate,
              })}
            </Typography>
          </Box>
        )}
      </Grid>
    </>
  );
};
